import pLimit from 'p-limit'
import { CircularProgressContinuous, CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { kGcsOrgHiddenFolder, kGcsUserFileUploadsBucketRef, kHiddenFileForFolderName, kUsersPath } from '@/constants/constants-gcs'
import { NewFolderPlus, Folder, AlertErrorCircle, DocumentFilled } from '@/assets/icons'
import { FullMetadata, StorageReference, deleteObject, getMetadata, listAll, ref, uploadBytes, list } from 'firebase/storage'
import { getAppStorage } from '@/firebase/storage-routing'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CreateFolderDialog from './create-folder-components/CreateFolderDialog'
import { useNavigate, useParams } from 'react-router-dom'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { downloadFile, copyFile } from './utils/files-utils'
import RenameFileDialog from './dialogs/rename-file'
import DeleteConfirmDialog from './dialogs/files-delete-confirm'
import FileDeleteConfirmDialog from './dialogs/single-file-delete-confirm'
import FilesActionsMenu from './FilesActionsMenu'
import { XClear } from '@/assets/icons'
import { validate as isValidUUID } from 'uuid'
import { selectMattersRecord, selectRenderedMatterNameMap } from '@/matter/store/selectors'
import { determineGcsFileStatus } from './utils/gcs-file-metadata-logic'
import { GCSFileMetadataStatusIndicator } from './utils/gcs-file-self-assessment'
import Breadcrumbs from './breadcrumbs/Breadcrumbs'
import { useViewFile } from './hooks/useViewFile'
import { hasViewableMarkdownFile, isPdfFile } from './utils/files-utils'
import { RootState } from '@/store/store'
import { kMyDriveName, matterPaths } from '@/routing/paths'
import { isInsideMatterFolder, isRootMatterFolder } from '@/content-viewer/components/common/save-to-drive-modal/utils/folder-name-utils'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'

type FilesListProps = {
  reloadTrigger: number
  currentFolderRef: StorageReference
}

type DisplayFolder = {
  folderRef: StorageReference
  displayName: string
}

export default function FilesList(props: FilesListProps) {
  const { currentFolderRef, reloadTrigger } = props
  const storage = getAppStorage({ bucketUrl: kGcsUserFileUploadsBucketRef, legacy_always_cloud: true })
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const viewFile = useViewFile()

  // Get matter and drive context from URL params
  const { matterId: urlMatterId, driveName } = useParams<{ matterId: string; driveName: string }>()

  // Derived Values
  // Build base drive location path using centralized path builder
  // urlMatterId is required since files routes always have matter context
  const baseDriveLocationPath = urlMatterId ? matterPaths.files.drive(urlMatterId, driveName ?? kMyDriveName) : '/dashboard/matters'

  // Get the rendered matter name map from Redux state
  const renderedMatterNameMap = useAppSelector(selectRenderedMatterNameMap)

  // Storage Ref Path For URI removes the first two parts of the path which are the root location for the drive
  function storageRefPathForUri(storageRef: StorageReference): string {
    const storagePath = storageRef.fullPath.split('/').slice(2).join('/')
    return encodeURIComponent(storagePath)
  }
  const storageRefSubFolderPath = storageRefPathForUri(currentFolderRef)
  const atDriveRoot = storageRefSubFolderPath === ''

  // State
  const [filesMetadata, setFilesMetadata] = useState<Record<string, FullMetadata>>({})
  const [loadingFilesMetadata, setLoadingFilesMetadata] = useState<boolean>(false)
  const [folderList, setFolderList] = useState<StorageReference[]>([]) // Intermediate state for folder references
  const [folders, setFolders] = useState<DisplayFolder[]>([])
  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState<boolean>(false)
  const [openRenameFileDialog, setOpenRenameFileDialog] = useState<boolean>(false)
  const [activeItem, setActiveItem] = useState<FullMetadata>()
  const [loading, setLoading] = useState<boolean>(false)

  // State - file and folder checkbox status
  // Folder - checked if all files are checked
  // File - checked if it is in the selectedFiles array
  const [selectedFiles, setSelectedFiles] = useState<string[]>([])
  const [selectedFolders, setSelectedFolders] = useState<string[]>([])

  const mattersRecord = useAppSelector((state: RootState) => selectMattersRecord(state))
  const matterIds = useMemo(() => Object.keys(mattersRecord), [mattersRecord])

  const currentMatter = urlMatterId ? mattersRecord[urlMatterId] : undefined
  const isInUnassignedMatter = isUnassignedMatter(currentMatter)

  // Display matter name instead of "My Drive" - show "Unassigned" for legacy unassigned matter
  const driveNameLabel = isInUnassignedMatter ? 'Unassigned' : currentMatter?.name ?? 'Files'

  const matterIdsSet = useMemo(() => new Set(matterIds), [matterIds])
  // Matter folders are not selectable, so compare against only the selectable folder count
  const selectableFolderCount = folders.filter((folder) => !isRootMatterFolder(folder.folderRef.fullPath, matterIdsSet)).length

  const allFilesAndFoldersSelected =
    (Object.keys(filesMetadata).length > 0 || selectableFolderCount > 0) &&
    selectedFiles.length === Object.keys(filesMetadata).length &&
    selectedFolders.length === selectableFolderCount

  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const [showSingleDeleteDialog, setShowSingleDeleteDialog] = useState<boolean>(false)

  // Check if there is content to show in the viewer
  const showContentViewer = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))

  // Ref to track the currently displayed folder to prevent race conditions with polling
  const displayedFolderRef = useRef(currentFolderRef)
  useEffect(() => {
    displayedFolderRef.current = currentFolderRef
  }, [currentFolderRef])

  // *******************************************
  // Get Files And Folders
  // Cached between renders with useCallback
  const getFilesAndFolders = useCallback(
    async (props: { changeLoadingState: boolean; storageRef: StorageReference }) => {
      const { changeLoadingState, storageRef } = props

      if (changeLoadingState) setLoading(true)

      try {
        // Get all the files
        const response = await listAll(storageRef)

        // Filter out hidden folders
        const filteredFolders = response.prefixes.filter((folder) => folder.name != kGcsOrgHiddenFolder)
        // Only update if the folder list has changed
        setFolderList(filteredFolders)

        // Start the loading of the files list
        setLoadingFilesMetadata(true)

        // Limit the number of concurrent metadata fetches to 10
        const limit = pLimit(10)

        // Get the metadata for each file
        const metadataPromises = response.items.map((itemRef) => {
          // Skip hidden files
          if (itemRef.name == kHiddenFileForFolderName) {
            return Promise.resolve(null)
          }

          return limit(async () => getMetadata(itemRef))
        })

        // Wait for all the metadata to be retrieved
        const metadataResultsWithNulls = await Promise.all(metadataPromises)
        const metadataResults = metadataResultsWithNulls.filter((metadata): metadata is FullMetadata => metadata !== null)

        // Convert the array of metadata into a dictionary keyed by the file's fullPath
        const metadataObject = metadataResults.reduce((acc, metadata) => {
          acc[metadata.fullPath] = metadata
          return acc
        }, {} as Record<string, FullMetadata>)

        setFilesMetadata(metadataObject)
      } catch (e) {
        console.error('Error getting files: ', e)
      } finally {
        setLoading(false)
        setLoadingFilesMetadata(false)
      }
    },
    [setFilesMetadata, setFolderList, setLoading]
  )

  // Memoize renderName
  const renderName = useCallback(
    (name: string, enable: boolean): string => {
      // Support dynamic enabling and check if the name is a UUID
      if (enable && isValidUUID(name)) {
        // If it is a UUID, return the rendered name from the map
        const renderedNameFromMap = renderedMatterNameMap.get(name)
        return renderedNameFromMap ? renderedNameFromMap : name
      }
      return name
    },
    [renderedMatterNameMap]
  )

  useEffect(() => {
    // Build Displayable Folders
    const processedFolders: DisplayFolder[] = folderList.map((folderRef) => {
      const isUserRootFolder = folderRef.fullPath.startsWith(kUsersPath) && folderRef.fullPath.split('/').length == 3
      const displayName = renderName(folderRef.name, isUserRootFolder)
      return { folderRef: folderRef, displayName }
    })
    processedFolders.sort((a, b) => a.displayName.localeCompare(b.displayName))
    setFolders(processedFolders) // Set the processed folders
  }, [folderList, renderedMatterNameMap, renderName])

  // Refresh the file list when the current folder changes + deselect all items from previous path
  useEffect(() => {
    getFilesAndFolders({ changeLoadingState: true, storageRef: currentFolderRef })
    setSelectedFiles([])
    setSelectedFolders([])
  }, [currentFolderRef, reloadTrigger, getFilesAndFolders])

  // This useEffect hook is responsible for clearing the files when the folder changes.
  useEffect(() => {
    setFilesMetadata({})
  }, [currentFolderRef])

  // Filter Out Abandoned File Overhaul Project Files
  // Files with customMetadata for file_name are part of an abandoned overhaul project and should not be displayed.
  const filesMetadataArray = Object.values(filesMetadata)
  const cleanedFilesList = filesMetadataArray.filter((file) => file.customMetadata?.file_name == null)
  cleanedFilesList.sort((a, b) => a.name.localeCompare(b.name))

  // *******************************************
  // Polling for processing files
  useEffect(() => {
    const pollMetadata = async (fileRef: StorageReference) => {
      try {
        const metadata = await getMetadata(fileRef)
        const status = determineGcsFileStatus({ fileMetadata: metadata })

        if (status.status === GCSFileMetadataStatusIndicator.PROCESSING) {
          // If still processing, wait 5 seconds and poll again
          await new Promise((res) => setTimeout(res, 5000))
          await pollMetadata(fileRef)
        } else {
          // When polling is complete, check if the file is in the currently displayed folder
          if (fileRef.parent?.fullPath === displayedFolderRef.current.fullPath) {
            // Update the state for this file in this folder
            setFilesMetadata((prev) => ({ ...prev, [metadata.fullPath]: metadata }))
          }
        }
      } catch (error) {
        console.error('Error polling for metadata:', error)
      }
    }

    // Find all files that are currently processing
    const processingFiles = Object.values(filesMetadata).filter((file) => {
      const status = determineGcsFileStatus({ fileMetadata: file })
      return status.status === GCSFileMetadataStatusIndicator.PROCESSING
    })

    // Start polling for each processing file
    processingFiles.forEach((file) => {
      const fileRef = ref(storage, file.fullPath)
      pollMetadata(fileRef)
    })
  }, [filesMetadata, storage])

  // *******************************************
  // Handle checkbox change for files
  function handleFileCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { checked, value } = event.target

    // Add or remove the file from the selected files array
    if (checked) {
      setSelectedFiles([...selectedFiles, value])
    } else {
      setSelectedFiles(selectedFiles.filter((file) => file != value))
    }
  }

  function handleFolderCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { checked, value } = event.target

    // Add or remove the folder from the selected folders array
    if (checked) {
      setSelectedFolders([...selectedFolders, value])
    } else {
      setSelectedFolders(selectedFolders.filter((folder) => folder != value))
    }
  }

  function selectAllFolders() {
    // Matter Bound Inputs enforcement - matter folders are not selectable to be deleted or renamed.
    const allFolders = folders.filter((folder) => !isRootMatterFolder(folder.folderRef.fullPath, matterIdsSet)).map((folder) => folder.folderRef.fullPath)
    setSelectedFolders(allFolders)
  }

  // Select all files
  function selectAllFiles() {
    const allFiles = filesMetadataArray.map((file) => file.fullPath)
    setSelectedFiles(allFiles)
  }

  // Function to rename a file
  async function handleRename(newName: string): Promise<void> {
    if (!activeItem) {
      console.error('No active item selected to rename')
      return
    }

    try {
      //Check if input value contains extension
      const hasExtension = /\.\w+$/.test(newName)
      const originalExtension = activeItem.fullPath.split('.')[1]
      if (originalExtension.length <= 1) throw new Error() //Error out in case we can't find a original file extension

      const newNameWithExtension = hasExtension ? newName : newName + `.${originalExtension}`

      const newPath = activeItem.fullPath.replace(/[^/]+$/, newNameWithExtension)

      await copyFile(activeItem.fullPath, newPath, storage)
      await deleteObject(ref(storage, activeItem.fullPath))

      setOpenRenameFileDialog(false)
      // Refresh the file list
      getFilesAndFolders({ changeLoadingState: false, storageRef: currentFolderRef })
    } catch (error) {
      setOpenRenameFileDialog(false)
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: `Unable to rename the file ${activeItem.name}`, durationMs: kToastDurationShortMs }))
      console.error('Error renaming file:', error)
    }
  }

  // Handle Delete Confirmation
  async function handleDeleteFolder(folder: string, deletePromises: Promise<void>[]) {
    const allFilesRef = ref(storage, folder)
    const allFilesResponse = await list(allFilesRef)

    // Directly delete any files in the folder
    allFilesResponse.items.forEach((file) => {
      deletePromises.push(deleteObject(file))
    })

    // Recursively delete subfolders
    for (const prefix of allFilesResponse.prefixes) {
      await handleDeleteFolder(prefix.fullPath, deletePromises)
    }

    return
  }

  // Handle Delete Confirmation
  async function handleDeleteFile() {
    if (!activeItem) {
      console.error('No active item selected to delete')
      return
    }

    // Delete the file
    const fileRef = ref(storage, activeItem.fullPath)
    await deleteObject(fileRef)
    dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `${activeItem.name} deleted successfully.`, durationMs: kToastDurationShortMs }))

    getFilesAndFolders({ changeLoadingState: false, storageRef: currentFolderRef })
  }

  async function handleDeleteConfirm() {
    console.log('Deleting files: ', selectedFiles)

    // Delete the files
    const deletePromises: Promise<void>[] = []
    selectedFiles.forEach((file) => {
      const fileRef = ref(storage, file)
      deletePromises.push(deleteObject(fileRef))
    })

    for (const folder of selectedFolders) {
      await handleDeleteFolder(folder, deletePromises)
    }

    // Wait for all the files to be deleted
    try {
      await Promise.all(deletePromises)
      dispatch(
        openGlobalToast({ type: GlobalToastType.SUCCESS, message: `${deletePromises.length} items deleted successfully.`, durationMs: kToastDurationShortMs })
      )
    } catch (e) {
      dispatch(
        openGlobalToast({ type: GlobalToastType.ERROR, message: `Deletion action failed, please refresh and try again.`, durationMs: kToastDurationShortMs })
      )
      throw e
    } finally {
      // Clear the selected files and folders
      setSelectedFiles([])
      setSelectedFolders([])

      // Refresh the file list
      getFilesAndFolders({ changeLoadingState: false, storageRef: currentFolderRef })
    }
  }

  async function handleFileDownload(file: FullMetadata) {
    //Second check here with file_name to not be undefined.

    try {
      await downloadFile(file.fullPath, file.name)
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `${file.name} downloaded successfully`, durationMs: kToastDurationShortMs }))
    } catch (e) {
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: `${file.name} download failed`, durationMs: kToastDurationShortMs }))
      throw e
    }
  }

  if (cleanedFilesList.length == 0 && folders.length == 0 && loading)
    return (
      <div className="flex justify-center items-center h-full">
        <CircularProgressContinuous />
      </div>
    )

  const fileFiles = selectedFiles.length == 1 ? 'file' : 'files'
  const folderFolders = selectedFolders.length == 1 ? 'folder' : 'folders'
  const toolbarDisabled = selectedFiles.length + selectedFolders.length == 0

  // Grid styles
  const headerGridCols = showContentViewer
    ? 'grid-cols-[19px_auto_34px]'
    : 'grid-cols-[19px_auto_0px_0px_34px] sm:grid-cols-[19px_auto_166px_0px_34px] md:grid-cols-[19px_auto_175px_82px_34px]'

  const folderGridCols = showContentViewer ? 'grid-cols-[100%]' : 'grid-cols-[100%_0px_0px] sm:grid-cols-[auto_175px_0px] md:grid-cols-[auto_175px_90px]'

  const fileGridCols = showContentViewer
    ? 'grid-cols-[auto_34px]'
    : 'grid-cols-[auto_0px_0px_34px] sm:grid-cols-[auto_175px_0px_34px] md:grid-cols-[auto_175px_90px_34px]'

  return (
    <div>
      {/* Actions Dialog */}
      <DeleteConfirmDialog
        title={selectedFiles.length > 1 || selectedFolders.length > 1 ? 'Delete Files Permanently?' : 'Delete File Permanently?'}
        fileMessage={selectedFiles.length > 0 ? `${selectedFiles.length} ${fileFiles} will be deleted forever.` : null}
        folderMessage={selectedFolders.length > 0 ? `${selectedFolders.length} ${folderFolders} will be deleted forever.` : null}
        onConfirm={handleDeleteConfirm}
        closeDialog={() => setShowDeleteDialog(false)}
        visible={showDeleteDialog}
      />
      <FileDeleteConfirmDialog
        fileName={activeItem?.name}
        onConfirm={handleDeleteFile}
        closeDialog={() => setShowSingleDeleteDialog(false)}
        visible={showSingleDeleteDialog}
      />
      <RenameFileDialog open={openRenameFileDialog} onSave={(data) => handleRename(data.itemName)} onClose={() => setOpenRenameFileDialog(false)} />

      <CreateFolderDialog
        open={openCreateFolderDialog}
        onSave={async (data) => {
          const { folderName } = data

          // Create the folder by creating a hidden file at its location
          const hiddenFilePath = `${currentFolderRef.fullPath}/${folderName}/${kHiddenFileForFolderName}`
          const hiddenFileRef = ref(storage, hiddenFilePath)
          const emptyContent = new Blob([]) // Create an empty blob

          // Upload the empty blob as a hidden file
          await uploadBytes(hiddenFileRef, emptyContent)

          // Close the dialog
          setOpenCreateFolderDialog(false)

          // Refresh the file list
          getFilesAndFolders({ changeLoadingState: false, storageRef: currentFolderRef })
        }}
        onClose={() => setOpenCreateFolderDialog(false)}
      />

      <>
        {/* New folder button */}
        <button
          onClick={() => setOpenCreateFolderDialog(true)}
          className="ml-4 py-2 w-auto rounded-md text-sm font-semibold text-brand-500 flex-nowrap disabled:text-true-gray-400 disabled:cursor-not-allowed"
          disabled={!isInsideMatterFolder(currentFolderRef.fullPath, new Set(matterIds)) || isInUnassignedMatter}
        >
          <NewFolderPlus className="inline text-lg pb-[2px]" /> <span className={'inline-block'}>New Folder</span>
        </button>

        {/* Breadcrumbs - shows matter name (or "Unassigned") as root, then folder path */}
        <div className="flex gap-1 overflow-hidden max-w-full ml-4">
          {atDriveRoot ? (
            // At root - show matter name only
            <div className={`font-semibold leading-6 text-sm transition-colors text-brand-neutral-900 text-nowrap`}>{driveNameLabel}</div>
          ) : (
            // In subfolder - Breadcrumbs component already shows matter name as first item
            <Breadcrumbs folderPath={currentFolderRef.fullPath || ''} />
          )}
        </div>
      </>

      {/* Toolbar */}
      <div className={`ml-4 pt-2 mb-1 flex gap-x-2 ${!toolbarDisabled ? 'opacity-100' : 'opacity-10'} transition-all duration-200 ease-in-out z-10`}>
        <button
          onClick={() => {
            setSelectedFiles([])
            setSelectedFolders([])
          }}
          disabled={toolbarDisabled}
          className={`flex gap-x-1 py-1 pr-3 items-center rounded-full text-sm hover:border-brand-700 hover:text-brand-700 hover:bg-brand-50 transition-all duration-200 ease-in-out ${
            !toolbarDisabled ? 'cursor-pointer' : 'cursor-default'
          }`}
        >
          <XClear width={20} height={20} />
          <div className={'text-sm'}>{selectedFiles.length + selectedFolders.length} selected</div>
        </button>

        <button
          onClick={() => {
            setShowDeleteDialog(true)
          }}
          disabled={toolbarDisabled}
          className={`flex gap-x-2 px-2 py-1 pr-3  rounded-full text-sm hover:border-brand-700 hover:text-brand-700 hover:bg-brand-50 transition-all duration-200 ease-in-out ${
            !toolbarDisabled ? 'cursor-pointer' : 'cursor-default'
          }`}
        >
          <XClear width={20} height={20} />
          <div className={'text-sm'}>Delete</div>
        </button>
      </div>
      {/* Files table header */}
      <div className={`p-2 grid ${headerGridCols} items-end overflow-hidden`}>
        <div>
          <input
            type="checkbox"
            checked={allFilesAndFoldersSelected}
            className={`w-4 h-4 ${allFilesAndFoldersSelected ? 'opacity-100' : 'opacity-30'} hover:opacity-100 transition-all ease-in-out duration-200`}
            onChange={() => {
              if (allFilesAndFoldersSelected) {
                setSelectedFiles([])
                setSelectedFolders([])
              } else {
                selectAllFiles()
                selectAllFolders()
              }
            }}
            value={'test'}
          />
        </div>
        <p className={'text-sm px-2'}>Name</p>
        {!showContentViewer && <p className={'text-sm px-2 hidden sm:block'}>Last modified</p>}
        {!showContentViewer && <p className={'text-sm px-2 hidden md:block'}>File size</p>}
        <div>&nbsp;</div>
      </div>
      <div>
        {/* List Folders - filter out matter folders (only show subfolders within current context) */}
        {folders
          .filter((folder) => !isRootMatterFolder(folder.folderRef.fullPath, matterIdsSet))
          .map((folder) => {
            const isChecked = selectedFolders.includes(folder.folderRef.fullPath)
            const folderNavPath = `${baseDriveLocationPath}/${storageRefPathForUri(folder.folderRef)}`

            const navigateToFolder = () => {
              navigate(folderNavPath)
              setSelectedFiles([])
              setSelectedFolders([])
            }

            return (
              <div
                key={folder.folderRef.fullPath}
                className={`border-t-[1px] py-2 grid ${folderGridCols} items-center overflow-hidden cursor-pointer hover:bg-brand-neutral-100`}
              >
                <div className={'px-2 grid grid-cols-[22px_32px_auto] items-center'}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    className={`w-4 h-4 ${
                      isChecked ? 'opacity-100' : 'opacity-30'
                    } group-hover:opacity-100 transition-all ease-in-out duration-200 checked:bg-brand-500 rounded-sm`}
                    onChange={handleFolderCheckboxChange}
                    value={folder.folderRef.fullPath}
                  />
                  <Folder className="w-5 h-5 ml-1 text-brand-neutral-600" onClick={navigateToFolder} />
                  <p onClick={navigateToFolder} className={'text-sm overflow-hidden'}>
                    {folder.displayName}
                  </p>
                </div>
                {!showContentViewer && <p className={'text-sm px-2 hidden sm:block'}></p>}
                {!showContentViewer && <p className={'text-sm px-2 hidden md:block'}></p>}
              </div>
            )
          })}

        {cleanedFilesList.length + folders.length === 0 && !isInUnassignedMatter && (
          <p className={'text-sm px-4'}>Upload some files to populate this folder.</p>
        )}

        {loadingFilesMetadata && (
          <div className={'py-4 flex justify-center items-center'}>
            <CircularProgressContinuousSized size={16} thickness={7} />
          </div>
        )}

        {/* List Files */}
        {cleanedFilesList.map((file) => {
          // Format date
          const date: Date = new Date(file.timeCreated)
          const formattedDate = Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(date)

          // Format file size
          // If the file size is under 1mb, show it in kb
          // If the file size is over 1mb, show it in mb
          let formattedSize = ''
          if (file.size < 1000000) {
            formattedSize = `${Math.round(file.size / 1000)} KB`
          } else {
            formattedSize = `${(file.size / 1000000).toFixed(1)} MB`
          }

          // Get the file status
          const fileStatus = determineGcsFileStatus({ fileMetadata: file })
          const isProcessingError: boolean = fileStatus.status === GCSFileMetadataStatusIndicator.ERROR
          const processing: boolean = fileStatus.status === GCSFileMetadataStatusIndicator.PROCESSING
          const processingErrorMessage = isProcessingError ? fileStatus.errorMessage ?? 'There was an error processing this file.' : undefined
          const isChecked = selectedFiles.includes(file.fullPath)

          // Check if preview is supported
          const isPdf = isPdfFile(file)
          const isMarkdownSupported = hasViewableMarkdownFile(file)
          const isPreviewSupported = isPdf || isMarkdownSupported

          function DynamicIconForStatus() {
            switch (true) {
              case isProcessingError && !processing:
                return <AlertErrorCircle className={'size-5 ml-1 text-white bg-red-600 rounded-full shrink-0'} />

              case processing:
                return (
                  <div className={'flex h-[24px] w-[24px] pl-[4px] pt-[2px] shrink-0'}>
                    <CircularProgressContinuousSized size={16} thickness={7} />
                  </div>
                )

              default:
                return <DocumentFilled className="flex w-5 h-5 ml-1 shrink-0" />
            }
          }

          return (
            <div
              key={file.fullPath}
              className={`group border-t-[1px] py-[6px] grid ${fileGridCols} items-center overflow-hidden hover:bg-brand-neutral-100
              ${isPreviewSupported ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              onClick={isPreviewSupported ? async () => await viewFile(file) : undefined}
            >
              <div className={'px-2 grid grid-cols-[22px_30px_auto] items-center'}>
                {/* Microsoft onedrive style file selection */}
                <input
                  type="checkbox"
                  checked={isChecked}
                  className={`w-4 h-4 flex items-center justify-center ${
                    isChecked ? 'opacity-100' : 'opacity-30'
                  } group-hover:opacity-100 transition-all ease-in-out duration-200 checked:bg-brand-500 rounded-sm`}
                  onChange={handleFileCheckboxChange}
                  value={file.fullPath}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="contents text-left" title={file.name}>
                  <DynamicIconForStatus />
                  <div className={'text-sm overflow-hidden flex'}>
                    <div className={'min-w-0'}>
                      <div className={'truncate'}>{file.name}</div>
                      {processingErrorMessage ? (
                        <div className={'text-red-700 text-xs truncate'} title={processingErrorMessage}>
                          {processingErrorMessage}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              {!showContentViewer && (
                <>
                  <div className="text-sm px-2 hidden sm:block">{formattedDate}</div>
                  <div className="text-sm px-2 hidden md:block">{formattedSize}</div>
                </>
              )}
              <div onClick={(e) => e.stopPropagation()}>
                <FilesActionsMenu
                  onSelect={() => setActiveItem(file)}
                  onRenameClick={() => setOpenRenameFileDialog(true)}
                  onDownloadClick={() => handleFileDownload(file)}
                  onDeleteClick={() => {
                    setActiveItem(file)
                    setShowSingleDeleteDialog(true)
                  }}
                  onLogMetadata={() => console.log(file)}
                />
              </div>
            </div>
          )
        })}
        {/* table bottom padding */}
        <div className="h-10 border-t-[1px]" />
      </div>
    </div>
  )
}
